/*
 * Lighting equations adapted from LerSent001/holo-card assets/renderer/renderer.js.
 * https://github.com/LerSent001/holo-card
 * Creative inspiration: @乌托邦的香蕉 (Xiaohongshu / Bilibili).
 * Shopify adaptation: original product photography stays on one plane. No inferred
 * subject masks, generated typography, fabricated contours or layered-depth claim.
 *
 * MIT License — Copyright (c) 2026 Holo Card contributors
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { getPhoneCardProfile, phoneMotionMedia } from './loc-motion-profiles.js?v=approved-20260911';
const vertex = `attribute vec2 position; varying vec2 uv;
void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
const fragment = `precision mediump float;
varying vec2 uv; uniform sampler2D art; uniform vec2 view; uniform float power;
vec3 rainbow(float t){return .52+.48*cos(6.28318*(t+vec3(0.,.33,.67)));}
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
void main(){
 vec4 source=texture2D(art,uv);vec3 base=source.rgb;
 float phase=uv.x*.62+uv.y*.36+view.x*1.9+view.y*.8;
 vec3 spectrum=rainbow(phase*2.2);
 float f=uv.x*.65+uv.y*.4+view.x*1.9+view.y*.85;
 float light=pow(max(0.,1.-abs(fract(f+.16)-.5)*2.),3.);
 float micro=pow(hash(floor(uv*vec2(620.,868.))),28.);
 base=mix(base,base*(.64+spectrum*.7)+spectrum*.07,light*.48*power);
 base+=spectrum*micro*light*.08*power;
 float glare=pow(max(0.,1.-length((uv-vec2(.5+view.x,.6+view.y))*vec2(1.,.75))),5.);
 base+=vec3(glare*.12*power);
 gl_FragColor=vec4(clamp(base,0.,1.)*source.a,source.a);
}`;
const cards = new Map();
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

function setup(card) {
  if (cards.has(card) || reduced.matches || card.hasAttribute('data-loc-layered')) return;
  const original = card.querySelector('.loc-ip__collectible-image');
  if (!original) return;
  const abort = new AbortController();
  const canvas = document.createElement('canvas');
  canvas.className = 'loc-holo-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  card.append(canvas);
  let gl, program, texture, buffer, frame = 0, disposed = false, visible = false;
  const shaders = [];
  let currentX = 0, currentY = 0, strength = 0;
  let targetX = 0, targetY = 0, targetStrength = 0;
  let motionActive = false;
  let observer;
  const cleanup = () => {
    disposed = true;
    abort.abort(); observer?.disconnect(); intersection.unobserve(card);
    cancelAnimationFrame(frame);
    if (gl) {
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      shaders.forEach(shader => gl.deleteShader(shader));
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
    canvas.remove(); delete card.dataset.holoReady; cards.delete(card);
  };
  const refresh = () => {
    motionActive = card.classList.contains('is-motion-active') && !card.classList.contains('is-pointer-active');
    const active = card.classList.contains('is-pointer-active') || motionActive;
    targetX = active ? parseFloat(card.style.getPropertyValue('--ip-tilt-x')) || 0 : 2;
    targetY = active ? parseFloat(card.style.getPropertyValue('--ip-tilt-y')) || 0 : -9;
    targetStrength = active ? .85 * (getPhoneCardProfile(card)?.foil ?? 1) : 0;
    if (visible && program && !document.hidden) {
      if (motionActive) { cancelAnimationFrame(frame); paint(); }
      else if (!frame) frame = requestAnimationFrame(paint);
    }
  };
  function paint() {
    frame = 0;
    if (disposed || !visible || !program || document.hidden || gl.isContextLost()) return;
    if (motionActive) { currentX = targetX; currentY = targetY; strength = targetStrength; }
    else {
      currentX += (targetX-currentX)*.19; currentY += (targetY-currentY)*.19;
      strength += (targetStrength-strength)*.15;
    }
    gl.uniform2f(gl.getUniformLocation(program, 'view'), Math.sin(currentY*Math.PI/180)*.65, Math.sin(currentX*Math.PI/180)*.65);
    gl.uniform1f(gl.getUniformLocation(program, 'power'), strength);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (Math.abs(targetX-currentX)+Math.abs(targetY-currentY)+Math.abs(targetStrength-strength)>.003) frame=requestAnimationFrame(paint);
  }
  cards.set(card, {cleanup, setVisible(value) {visible=value; if(value) refresh(); else {cancelAnimationFrame(frame);frame=0;}}});
  intersection.observe(card);
  // A browser can cancel pointermove for a vertical pan. Prevent native scrolling
  // only AFTER the existing 280ms hold activates; ordinary swipes remain native.
  card.addEventListener('touchmove', event => {
    if (event.touches.length === 1 && card.classList.contains('is-pointer-active')) event.preventDefault();
  }, {passive:false, signal:abort.signal});
  card.addEventListener('dragstart', event => event.preventDefault(), {signal:abort.signal});
  canvas.addEventListener('webglcontextlost', cleanup, {once:true, signal:abort.signal});
  document.addEventListener('visibilitychange', refresh, {signal:abort.signal});
  phoneMotionMedia.addEventListener('change', refresh, {signal:abort.signal});
  const image = new Image(); image.crossOrigin='anonymous';
  image.onload = () => {
    if (disposed) return;
    try {
      gl=canvas.getContext('webgl', {alpha:true, premultipliedAlpha:true, antialias:false, preserveDrawingBuffer:true, powerPreference:'low-power'});
      if (!gl) throw new Error('WebGL unavailable');
      const compile = (type, source) => {
        const shader=gl.createShader(type); shaders.push(shader);
        gl.shaderSource(shader,source); gl.compileShader(shader);
        if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS)) throw new Error('Shader compilation failed');
        return shader;
      };
      program=gl.createProgram(); gl.attachShader(program,compile(gl.VERTEX_SHADER,vertex)); gl.attachShader(program,compile(gl.FRAGMENT_SHADER,fragment));
      gl.linkProgram(program); if(!gl.getProgramParameter(program,gl.LINK_STATUS)) throw new Error('Shader link failed');
      gl.useProgram(program);
      buffer=gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
      gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
      const position=gl.getAttribLocation(program,'position'); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
      texture=gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D,texture);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,image);
      canvas.width=Math.min(720,image.naturalWidth);canvas.height=Math.round(canvas.width*image.naturalHeight/image.naturalWidth);
      gl.viewport(0,0,canvas.width,canvas.height);gl.uniform1i(gl.getUniformLocation(program,'art'),0);
      // Match object-fit: contain for sealed product photographs of any aspect.
      canvas.style.objectFit=getComputedStyle(original).objectFit;
      card.dataset.holoReady='true';
      observer=new MutationObserver(refresh);observer.observe(card,{attributes:true,attributeFilter:['style','class']});
      refresh();
    } catch { cleanup(); }
  };
  image.onerror=cleanup;
  image.src=original.currentSrc || original.src;
}
const intersection = new IntersectionObserver(entries => {
  entries.forEach(entry => cards.get(entry.target)?.setVisible(entry.isIntersecting));
}, {rootMargin:'100px'});
const lazy = new IntersectionObserver(entries => {
  entries.forEach(entry => {if(entry.isIntersecting){lazy.unobserve(entry.target);setup(entry.target);}});
}, {rootMargin:'200px'});
function scan(root=document) {root.querySelectorAll('[data-ip-collectible]').forEach(card => lazy.observe(card));}
scan();
document.addEventListener('shopify:section:load', event => scan(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-ip-collectible]').forEach(card => {lazy.unobserve(card);cards.get(card)?.cleanup();});
});
reduced.addEventListener('change', () => {
  if(reduced.matches) [...cards.values()].forEach(card => card.cleanup()); else scan();
});
